import type { CreativeBriefResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CBFilterHandlers,
  CBFilterOptions,
  CBFilterState,
  SortDirection
} from './CreativeBriefs.types';
import { SORT_DIRECTION } from './CreativeBriefs.types';
import useCreativeBriefsApi from './useCreativeBriefsApi';

type FilterArrayKey =
  | 'selectedStrategyIds'
  | 'selectedProductIds'
  | 'selectedFormats'
  | 'selectedMonths';

// ─── Constants ───────────────────────────────────────────────────────────────

export const DEFAULT_FILTER_STATE: CBFilterState = {
  selectedStrategyIds: [],
  selectedProductIds: [],
  selectedFormats: [],
  selectedMonths: [],
  sortDirection: SORT_DIRECTION.Desc,
  pageNumber: 1,
  pageSize: 21,
  visibleToCustomerOnly: false
};

const EMPTY_FILTER_OPTIONS: CBFilterOptions = {
  strategies: [],
  products: [],
  formats: [],
  months: []
};

// ─── Hook ────────────────────────────────────────────────────────────────────

const useCBFilterState = () => {
  const [filterState, setFilterState] = useState<CBFilterState>(DEFAULT_FILTER_STATE);
  const [briefs, setBriefs] = useState<CreativeBriefResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState<CBFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [error, setError] = useState<string | null>(null);

  const { getCreativeBriefsInternal, getFilterCountsInternal, loading, brandId } =
    useCreativeBriefsApi();

  // Refs hold latest fetch functions to avoid stale closures in effects.
  const getFilterCountsRef = useRef(getFilterCountsInternal);
  const getBriefsRef = useRef(getCreativeBriefsInternal);
  getFilterCountsRef.current = getFilterCountsInternal;
  getBriefsRef.current = getCreativeBriefsInternal;

  // Fetch filter counts when brandId or visibleToCustomerOnly changes.
  // Unlike the web app, we re-fetch counts when the visibility toggle changes
  // because the internal endpoint supports filtering by visibility.
  useEffect(() => {
    if (!brandId) return () => {};

    let ignore = false;

    (async () => {
      const result = await getFilterCountsRef.current(
        filterState.visibleToCustomerOnly || undefined
      );
      if (ignore) return;
      if (!result.data) return;
      const { strategies, products, formats, months } = result.data;
      setFilterOptions({ strategies, products, formats, months });
    })();

    return () => {
      ignore = true;
    };
  }, [brandId, filterState.visibleToCustomerOnly]);

  // Fetch briefs whenever filterState or brandId changes.
  useEffect(() => {
    if (!brandId) return () => {};

    let ignore = false;
    const params = {
      pageSize: filterState.pageSize,
      pageNumber: filterState.pageNumber,
      sortDirection: filterState.sortDirection,
      strategyIds: filterState.selectedStrategyIds,
      productIds: filterState.selectedProductIds,
      formats: filterState.selectedFormats,
      months: filterState.selectedMonths,
      visibleToCustomerOnly: filterState.visibleToCustomerOnly || undefined
    };

    (async () => {
      const result = await getBriefsRef.current(params);
      if (ignore) return;
      if (result.error) {
        setError(result.error);
        return;
      }
      if (!result.data) return;
      setBriefs(result.data.data);
      setTotal(result.data.total);
      setError(null);
    })();

    return () => {
      ignore = true;
    };
  }, [brandId, filterState]);

  const makeToggle = useCallback(
    <K extends FilterArrayKey>(key: K) =>
      (value: CBFilterState[K][number]) =>
        setFilterState(prev => {
          const current: ReadonlyArray<CBFilterState[K][number]> = prev[key];
          const next = current.includes(value)
            ? (current.filter(v => v !== value) as CBFilterState[K])
            : ([...current, value] as CBFilterState[K]);
          return { ...prev, [key]: next, pageNumber: 1 };
        }),
    []
  );

  const handlers: CBFilterHandlers = useMemo(
    () => ({
      toggleStrategyId: makeToggle('selectedStrategyIds'),
      toggleProductId: makeToggle('selectedProductIds'),
      toggleFormat: makeToggle('selectedFormats'),
      toggleMonth: makeToggle('selectedMonths'),
      setSortDirection: (dir: SortDirection) =>
        setFilterState(prev => ({ ...prev, sortDirection: dir, pageNumber: 1 })),
      setPage: (page: number) => setFilterState(prev => ({ ...prev, pageNumber: page })),
      resetFilters: () => setFilterState(DEFAULT_FILTER_STATE),
      setVisibleToCustomerOnly: (value: boolean) =>
        setFilterState(prev => ({ ...prev, visibleToCustomerOnly: value, pageNumber: 1 }))
    }),
    [makeToggle]
  );

  return { filterState, filterOptions, briefs, setBriefs, total, loading, error, handlers };
};

export default useCBFilterState;
